import { useEffect, useRef } from "react"
import { useLocation } from "react-router-dom"
import {
  frontUpdate,
  isEditableField,
  SERVED_VERSION_CHECK_INTERVAL_MS,
  servedVersionOf,
} from "@app/core/services/front-update"

const EVALUATE_INTERVAL_MS = 30 * 1000
/** Timers of a hidden tab still run once a minute: a longer gap means the computer slept or the tab was frozen. */
const SLEEP_GAP_MS = 3 * 60 * 1000
const INTERACTION_EVENTS = ["pointerdown", "pointermove", "keydown", "wheel", "touchstart"]

async function readServedVersion(): Promise<void> {
  try {
    const response = await fetch("/index.html", { cache: "no-store" })
    if (response.ok) {
      frontUpdate.servedVersionRead(servedVersionOf(await response.text()))
    }
  } catch {
    // The server is being deployed or the network is down: read again later
  }
}

/**
 * Feeds frontUpdate with what happens in the page: versions served, navigation, typing, idleness, the page shown again.
 */
export function useFrontUpdate(): void {
  const { pathname } = useLocation()
  const previousPathname = useRef(pathname)

  useEffect(() => {
    if (previousPathname.current !== pathname) {
      previousPathname.current = pathname
      frontUpdate.navigated()
    }
  }, [pathname])

  useEffect(() => {
    const onInteraction = (): void => frontUpdate.userInteracted()
    const onInput = (event: Event): void => {
      if (isEditableField(event.target)) {
        frontUpdate.inputEdited()
      }
    }
    const onShownAgain = (): void => {
      frontUpdate.pageShown()
      readServedVersion()
    }
    const onVisibilityChange = (): void => {
      if (document.visibilityState === "visible") {
        onShownAgain()
      } else {
        frontUpdate.evaluate()
      }
    }
    const onPreloadError = (event: Event): void => {
      if (frontUpdate.fileOfThisVersionMissing()) {
        // The page reloads: the failed import must not reach the error boundary in the meantime
        event.preventDefault()
      }
    }

    let lastTickAt = Date.now()
    const evaluateTimer = window.setInterval(() => {
      const now = Date.now()
      const slept = now - lastTickAt > SLEEP_GAP_MS
      lastTickAt = now
      if (slept) {
        onShownAgain()
      } else {
        frontUpdate.evaluate()
      }
    }, EVALUATE_INTERVAL_MS)
    const readTimer = window.setInterval(readServedVersion, SERVED_VERSION_CHECK_INTERVAL_MS)

    INTERACTION_EVENTS.forEach((type) => window.addEventListener(type, onInteraction, { capture: true, passive: true }))
    document.addEventListener("input", onInput, true)
    document.addEventListener("change", onInput, true)
    document.addEventListener("visibilitychange", onVisibilityChange)
    window.addEventListener("online", readServedVersion)
    window.addEventListener("vite:preloadError", onPreloadError)

    return () => {
      window.clearInterval(evaluateTimer)
      window.clearInterval(readTimer)
      INTERACTION_EVENTS.forEach((type) => window.removeEventListener(type, onInteraction, { capture: true }))
      document.removeEventListener("input", onInput, true)
      document.removeEventListener("change", onInput, true)
      document.removeEventListener("visibilitychange", onVisibilityChange)
      window.removeEventListener("online", readServedVersion)
      window.removeEventListener("vite:preloadError", onPreloadError)
    }
  }, [])
}
